/**
 * CrmDashboard - Dashboard principal do módulo CRM
 * 
 * Container principal que gerencia múltiplos dashboards via sistema de abas.
 * Exibe dashboards baseado nas permissões do usuário.
 * 
 * @author Sistema
 * @version 2.3.0 - Atualização inclui as consultas independentes do funil e evolução
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, LayoutDashboard, Plus, X, RefreshCw, Clock } from 'lucide-react';
import { useModuleDashboards } from '@/hooks/useDashboards';
import { DashboardTabs, DashboardRenderer } from '@/components/crm/dashboard';
import { Button } from '@/components/ui/button';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { useCrmOverviewFilters } from '@/hooks/useCrmOverviewFilters';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { cn } from '@/lib/utils';
import {
  CrmPeriodFilter,
  CrmWorkspaceFilter,
  CrmSellerFilter,
} from '@/components/crm/filters';

/**
 * Página principal do Dashboard CRM
 * Integra o sistema de abas com renderização dinâmica de dashboards
 */
export default function CrmDashboard() {
  const { t } = useTranslation('crm');
  const queryClient = useQueryClient();
  
  // Estado do modal de novo negócio
  const [formOpen, setFormOpen] = useState(false);
  
  // Hook para refresh com timestamp (v2.2.0)
  const { isRefreshing, formattedTime, triggerRefresh } = useRefreshWithTimestamp();
  
  // Hook para gerenciar dashboards disponíveis
  const {
    dashboards,
    selectedDashboard,
    selectedDashboardId,
    selectDashboard,
    isLoading,
  } = useModuleDashboards('crm');

  // Hook para gerenciar filtros do dashboard (compartilhado com CrmDashboardOverview)
  const {
    filters,
    setPeriodPreset,
    setCustomPeriod,
    setWorkspaceId,
    setSellerId,
    clearFilters,
    hasActiveFilters
  } = useCrmOverviewFilters();

  // Função para atualizar dados do dashboard (v2.2.0)
  const handleRefresh = async () => {
    await triggerRefresh(async () => {
      // Invalida todas as queries relacionadas ao CRM dashboard
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['crm-dashboard-stats-v2'] }),
        queryClient.invalidateQueries({ queryKey: ['crm-overview-stats'] }),
        queryClient.invalidateQueries({ queryKey: ['overview-conversion-funnel'] }),
        queryClient.invalidateQueries({ queryKey: ['overview-monthly-evolution'] }),
        queryClient.invalidateQueries({ queryKey: ['crm-overview-kpis'] }),
        queryClient.invalidateQueries({ queryKey: ['crm-pipeline-by-workspace'] }),
        queryClient.invalidateQueries({ queryKey: ['seller-performance-stats'] }),
        queryClient.invalidateQueries({ queryKey: ['my-performance-stats'] }),
        queryClient.invalidateQueries({ queryKey: ['recent-won-deals'] }),
        queryClient.invalidateQueries({ queryKey: ['crm-dashboards'] }),
      ]);
    });
  };

  // Estado de carregamento inicial
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Sem dashboards disponíveis
  if (dashboards.length === 0) {
    return (
      <div className="space-y-4">
        {/* Modal de novo negócio */}
        <DealFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          deal={null}
        />

        <div className="flex items-center justify-between">
          <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('dashboard.title', 'Dashboard CRM')}
          </h1>
        </div>
        <Button 
          size="xs"
          onClick={() => setFormOpen(true)}
          className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
        >
          <Plus className="mr-1.5" />
            {t('dashboard.newDeal', 'Novo Negócio')}
          </Button>
        </div>
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <LayoutDashboard className="h-16 w-16 mb-4 opacity-20" />
          <p className="text-lg font-medium">
            {t('dashboard.noDashboards', 'Nenhum dashboard disponível')}
          </p>
          <p className="text-sm">
            {t('dashboard.contactAdmin', 'Entre em contato com o administrador para configurar seus dashboards.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full min-w-0 overflow-x-hidden">
      {/* Modal de novo negócio */}
      <DealFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        deal={null}
      />

      {/* Cabeçalho responsivo: mobile em 2 linhas, desktop em 1 linha */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
        {/* Linha 1 (mobile): Título + Refresh | Botão */}
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="flex items-center gap-1.5 shrink-0 min-w-0">
            <h1 className="text-lg md:text-2xl font-bold tracking-tight truncate">
              {t('dashboard.title', 'Dashboard CRM')}
            </h1>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              title="Atualizar dados"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", (isRefreshing || isLoading) && "animate-spin")} />
            </Button>
            <span className="text-[10px] hidden md:inline-flex items-center gap-1 text-muted-foreground">
              <Clock className="h-2.5 w-2.5" />
              {formattedTime}
            </span>
          </div>

          {/* Botão Novo Negócio - mobile fica à direita na mesma linha do título */}
          <Button
            size="xs"
            onClick={() => setFormOpen(true)}
            className="ml-auto sm:hidden shrink-0 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Separador visual apenas em desktop */}
        <span className="text-muted-foreground/30 shrink-0 hidden sm:inline">|</span>

        {/* Filtros com scroll horizontal */}
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none min-w-0 flex-1 -mx-1 px-1">
          <CrmPeriodFilter
            value={filters.period}
            onPresetChange={setPeriodPreset}
            onCustomChange={setCustomPeriod}
            className="h-[22px] text-xs px-2"
          />
          <CrmWorkspaceFilter
            value={filters.workspaceId}
            onChange={setWorkspaceId}
            className="h-[22px] text-xs px-2"
          />
          <CrmSellerFilter
            value={filters.sellerId}
            onChange={setSellerId}
            className="h-[22px] text-xs px-2"
          />
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-[22px] px-1.5 text-xs text-muted-foreground hover:text-foreground shrink-0"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Botão Novo Negócio - desktop */}
        <Button
          size="xs"
          onClick={() => setFormOpen(true)}
          className="hidden sm:inline-flex shrink-0 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
        >
          <Plus className="sm:mr-1.5" />
          <span className="hidden sm:inline">{t('dashboard.newDeal', 'Novo Negócio')}</span>
        </Button>
      </div>


      {/* Tabs de navegação entre dashboards */}
      <DashboardTabs
        dashboards={dashboards}
        selectedDashboardId={selectedDashboardId}
        onSelectDashboard={selectDashboard}
        isLoading={isLoading}
      />

      {/* Conteúdo do dashboard selecionado */}
      {/* Conteúdo do dashboard selecionado - passa filtros globais */}
      {selectedDashboard && (
        <DashboardRenderer
          componentKey={selectedDashboard.component_key}
          dashboardId={selectedDashboard.id}
          filters={filters}
        />
      )}
    </div>
  );
}
